import { type FC, type ReactNode, useMemo, useCallback, useEffect, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

import {
    ScrollCarouselProvider,
    useScrollCarousel,
} from 'dibs-scroll-carousel/exports/ScrollCarouselContext.ts';
import { InternalScrollCarouselProvider } from 'dibs-scroll-carousel/exports/InternalScrollCarouselContext.ts';
import { ScrollContainer } from 'dibs-scroll-carousel/exports/ScrollContainer.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { useItemMediaItems, isVimeoVideo } from 'dibs-image-utils/exports/useItemMediaItems.ts';

import { HeroCarouselThumb } from './HeroCarouselThumb.tsx';
import { HeroCarouselImage } from './HeroCarouselImage.tsx';
import { ViewInRoom } from '../../respShared/PdpRespSharedHeroCarousel/CarouselItems/ViewInRoom.tsx';
import { VideoContainer } from '../../respShared/PdpRespSharedHeroCarousel/CarouselItems/VideoContainer.tsx';
import { HeroCarouselRequestMore } from './HeroCarouselRequestMore.tsx';
import { PdpSharedImageGalleryModalLazy } from '../../PdpSharedImageGalleryModal/PdpSharedImageGalleryModalLazy.tsx';
import { usePdpSharedGalleryModalController } from '../../PdpSharedImageGalleryModal/usePdpSharedGalleryModalController.ts';
import { ThumbScrollArrow } from './ThumbScrollArrow.tsx';
import { HeroCarouselArrow } from './HeroCarouselArrow.tsx';
import { CarouselItemWrapper } from './CarouselItemWrapper.tsx';
import { useThumbsPanel } from './useThumbsPanel.ts';
import { isPhoto, type CarouselItem } from './types.ts';
import { SIZES, SRC_SET_WIDTHS, THUMBS_VISIBLE } from './constants.ts';
import { PdpSharedMediaCountPill } from '../../shared/PdpSharedMediaCountPill/PdpSharedMediaCountPill.tsx';
import { getMediaCountsFromCarouselItems } from '../../shared/PdpSharedMediaCountPill/getMediaCounts.ts';
import { trackPrimaryImagePillClick } from '../../tracking/imageGalleryTracking.ts';

import { type PdpRespHeroCarousel_item$key } from './__generated__/PdpRespHeroCarousel_item.graphql.ts';
import { type PdpRespHeroCarousel_viewer$key } from './__generated__/PdpRespHeroCarousel_viewer.graphql.ts';
import { type PdpRespHeroCarousel_user$key } from './__generated__/PdpRespHeroCarousel_user.graphql.ts';

import styles from './PdpRespHeroCarousel.scss';

const viewerFragment = graphql`
    fragment PdpRespHeroCarousel_viewer on Viewer {
        ...PdpSharedImageGalleryModal_viewer
        vimeoItemsVideoHLSPlayerFF: featureFlag(feature: "vimeoItemsVideoHLSPlayer")
    }
`;

const itemFragment = graphql`
    fragment PdpRespHeroCarousel_item on Item {
        ...useItemMediaItems_item
        ...HeroCarouselImage_item
        ...ViewInRoom_item
        ...HeroCarouselRequestMore_item
        ...PdpSharedImageGalleryModal_item
        sortedPhotos {
            ...useItemMediaItems_sortedPhotos
            ...HeroCarouselThumb_sortedPhoto
            ...HeroCarouselImage_sortedPhoto
            __typename
            masterOrZoomPath
            largePath
            alt
        }
        # video thumbnail/id fields below are consumed by the gallery via the
        # useItemMediaItems() carousel items, so relay/unused-fields is disabled for them
        videoVimeo(convertToVimeoFormat: false) {
            ...useItemMediaItems_videoVimeo
            ...HeroCarouselThumb_videoVimeo
            ...VideoContainer_videoVimeo
            # eslint-disable-next-line relay/unused-fields
            galleryThumbnail: thumbnail(size: w1280h720, includePlayButton: false, pad: false)
            # eslint-disable-next-line relay/unused-fields
            posterThumbnail: thumbnail(size: w1280h720, includePlayButton: false, pad: true)
            # eslint-disable-next-line relay/unused-fields
            thumbnail(size: w100h75, includePlayButton: false, pad: false)
            videoUrl: fileByVideoSize(size: adaptive) {
                link
            }
            # eslint-disable-next-line relay/unused-fields
            vimeoId
            width
            height
        }
        videoMux {
            ...useItemMediaItems_muxVideo
            ...HeroCarouselThumb_muxVideo
            # eslint-disable-next-line relay/unused-fields
            galleryThumbnail: thumbnail(width: 1280, height: 720, fitMode: smartcrop)
            # eslint-disable-next-line relay/unused-fields
            posterThumbnail: thumbnail(width: 1280, height: 720)
            # eslint-disable-next-line relay/unused-fields
            thumbnail(width: 100, height: 75)
            url
            width
            height
        }
        viewInRoomPhotos {
            ...useItemMediaItems_viewInRoomPhotos
            ...HeroCarouselThumb_viewInRoomPhoto
            ...ViewInRoom_viewInRoomPhoto
            ...PdpSharedImageGalleryViewInRoomImage_viewInRoomPhoto
        }
        hasPrivateListing
        seller {
            isAccessible
        }
    }
`;

const userFragment = graphql`
    fragment PdpRespHeroCarousel_user on User {
        ...ViewInRoom_user
        ...HeroCarouselRequestMore_user
        ...PdpSharedImageGalleryModal_user
    }
`;

const PdpRespHeroCarouselInner: FC<{
    item: PdpRespHeroCarousel_item$key;
    user: PdpRespHeroCarousel_user$key | null | undefined;
    viewer: PdpRespHeroCarousel_viewer$key;
    carouselItems: CarouselItem[];
    currency: string;
    disableModals?: boolean;
    afterDisplay: boolean;
    selectedSkuId?: string | null;
    imageOverlay?: ReactNode;
    enableSticky?: boolean;
    children?: ReactNode;
}> = ({
    item: itemRef,
    user: userRef,
    viewer: viewerRef,
    carouselItems,
    currency,
    disableModals,
    afterDisplay,
    selectedSkuId,
    imageOverlay,
    enableSticky,
    children,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const vimeoItemsVideoHLSPlayerFF = !!viewer?.vimeoItemsVideoHLSPlayerFF;

    const { activeSlide, totalSlides, resetCurrentIndex, setCurrentIndex } = useScrollCarousel();

    const {
        isGridOpen,
        isZoomOpen,
        canMountGalleryModal,
        selectedIndex,
        setSelectedIndex,
        openZoom,
        closeZoom,
        openGalleryModal,
        handleCloseGrid,
    } = usePdpSharedGalleryModalController({
        disableModals,
        onRestoreIndex: index => {
            const backfillStartCount = Math.min(carouselItems.length, THUMBS_VISIBLE);
            setCurrentIndex({ index: index + backfillStartCount, backfillStartCount });
        },
    });

    const openGalleryModalAtActiveSlide = useCallback(() => {
        openGalleryModal(activeSlide - 1);
    }, [openGalleryModal, activeSlide]);

    const openGalleryModalFromPill = (): void => {
        trackPrimaryImagePillClick();
        openGalleryModalAtActiveSlide();
    };

    const selectedSkuIdRef = useRef(selectedSkuId);
    useEffect(() => {
        if (selectedSkuIdRef.current !== selectedSkuId) {
            selectedSkuIdRef.current = selectedSkuId;
            resetCurrentIndex();
        }
    }, [resetCurrentIndex, selectedSkuId]);

    const {
        thumbsScrollRef,
        canScrollUp,
        canScrollDown,
        scrollThumbsUp,
        scrollThumbsDown,
        hasOverflow,
        handleThumbSelect,
        onArrowClick,
    } = useThumbsPanel({ carouselItems, afterDisplay });

    const { photoCount, hasVideo } = getMediaCountsFromCarouselItems(carouselItems);

    return (
        <>
            {canMountGalleryModal && (isGridOpen || isZoomOpen) && (
                <PdpSharedImageGalleryModalLazy
                    isOpen={isGridOpen}
                    isZoomOpen={isZoomOpen}
                    isMobile={false}
                    photoSizes={SIZES}
                    photoSrcSetWidths={SRC_SET_WIDTHS}
                    startingIndex={selectedIndex}
                    carouselItems={carouselItems}
                    item={item}
                    user={user}
                    viewer={viewer}
                    currency={currency}
                    showRequestMore={false}
                    disableModals={disableModals}
                    onCloseGrid={handleCloseGrid}
                    onCloseZoom={closeZoom}
                    onOpenZoom={openZoom}
                    onIndexChange={setSelectedIndex}
                />
            )}

            <div
                className={classnames(
                    dibsCss.flex,
                    dibsCss.flexRow,
                    dibsCss.itemsStart,
                    dibsCss.gapXsmall,
                    dibsCss.wFull,
                    dibsCss.minW0,
                    dibsCss.m0,
                    dibsCss.p0,
                    enableSticky && dibsCss.zLow,
                    enableSticky && styles.stickyHeroBundle
                )}
            >
                {/* Thumbs column */}
                <div className={classnames(dibsCss.relative, styles.thumbsColumn)}>
                    {afterDisplay && (
                        <>
                            {hasOverflow && canScrollUp && (
                                <ThumbScrollArrow direction="up" onClick={scrollThumbsUp} />
                            )}
                            <div
                                className={classnames(
                                    dibsCss.hFull,
                                    dibsCss.overflowYAuto,
                                    dibsCss.overflowXHidden,
                                    dibsCss.flex,
                                    dibsCss.flexCol,
                                    dibsCss.gapXsmall,
                                    styles.thumbsList
                                )}
                                ref={thumbsScrollRef}
                            >
                                {carouselItems.map((carouselItem, index) => {
                                    const commonProps = {
                                        index,
                                        isActive: index === activeSlide - 1,
                                        totalSlides,
                                        onSelect: handleThumbSelect,
                                    } as const;

                                    if (carouselItem.__type === 'photo') {
                                        return (
                                            <HeroCarouselThumb
                                                key={index}
                                                type={carouselItem.__type}
                                                sortedPhoto={carouselItem}
                                                videoVimeo={null}
                                                muxVideo={null}
                                                viewInRoomPhoto={null}
                                                {...commonProps}
                                            />
                                        );
                                    } else if (carouselItem.__type === 'video') {
                                        if (isVimeoVideo(carouselItem)) {
                                            return (
                                                <HeroCarouselThumb
                                                    key={index}
                                                    type={carouselItem.__type}
                                                    sortedPhoto={null}
                                                    videoVimeo={carouselItem}
                                                    muxVideo={null}
                                                    viewInRoomPhoto={null}
                                                    {...commonProps}
                                                />
                                            );
                                        } else {
                                            return (
                                                <HeroCarouselThumb
                                                    key={index}
                                                    type={carouselItem.__type}
                                                    sortedPhoto={null}
                                                    videoVimeo={null}
                                                    muxVideo={carouselItem}
                                                    viewInRoomPhoto={null}
                                                    {...commonProps}
                                                />
                                            );
                                        }
                                    } else if (carouselItem.__type === 'viewInRoom') {
                                        return (
                                            <HeroCarouselThumb
                                                key={index}
                                                type={carouselItem.__type}
                                                sortedPhoto={null}
                                                videoVimeo={null}
                                                muxVideo={null}
                                                viewInRoomPhoto={carouselItem}
                                                {...commonProps}
                                            />
                                        );
                                    } else if (carouselItem.__type === 'requestMore') {
                                        return (
                                            <HeroCarouselThumb
                                                key={index}
                                                type={carouselItem.__type}
                                                sortedPhoto={null}
                                                videoVimeo={null}
                                                muxVideo={null}
                                                viewInRoomPhoto={null}
                                                {...commonProps}
                                            />
                                        );
                                    }
                                    return <div key={index} />;
                                })}
                            </div>
                            {hasOverflow && canScrollDown && (
                                <ThumbScrollArrow direction="down" onClick={scrollThumbsDown} />
                            )}
                        </>
                    )}
                </div>

                {/* Main carousel */}
                <div
                    className={classnames(
                        dibsCss.flex1,
                        dibsCss.minW0,
                        dibsCss.flex,
                        dibsCss.flexCol
                    )}
                >
                    <div
                        className={classnames(
                            dibsCss.relative,
                            dibsCss.roundedXxsmall,
                            dibsCss.overflowHidden,
                            styles.displayContainer
                        )}
                    >
                        <div className={classnames(dibsCss.absolute, dibsCss.inset0)}>
                            <InternalScrollCarouselProvider
                                orientation="horizontal"
                                itemsToShow={1}
                                animation="none"
                            >
                                <div
                                    className={classnames(
                                        dibsCss.relative,
                                        dibsCss.flex,
                                        dibsCss.itemsCenter,
                                        dibsCss.justifyCenter,
                                        dibsCss.hFull,
                                        dibsCss.wFull
                                    )}
                                    data-tn="pdp-image-carousel"
                                    data-active-slide={activeSlide}
                                >
                                    <HeroCarouselArrow
                                        type="previous"
                                        onArrowClick={onArrowClick}
                                    />
                                    <ScrollContainer>
                                        {carouselItems.map((carouselItem, index) => {
                                            let child = null;
                                            if (carouselItem.__type === 'photo') {
                                                child = (
                                                    <HeroCarouselImage
                                                        eagerLoad={index === 0}
                                                        item={item}
                                                        sortedPhoto={carouselItem}
                                                    />
                                                );
                                            } else if (carouselItem.__type === 'video') {
                                                if (isVimeoVideo(carouselItem)) {
                                                    child = (
                                                        <VideoContainer
                                                            vimeoItemsVideoHLSPlayerFF={
                                                                vimeoItemsVideoHLSPlayerFF
                                                            }
                                                            videoVimeo={carouselItem}
                                                            videoUrl={
                                                                carouselItem.videoUrl?.link || ''
                                                            }
                                                            heightOffset={0}
                                                            paused={
                                                                vimeoItemsVideoHLSPlayerFF &&
                                                                index !== activeSlide - 1
                                                            }
                                                            width={carouselItem.width}
                                                            height={carouselItem.height}
                                                        />
                                                    );
                                                } else {
                                                    child = (
                                                        <VideoContainer
                                                            vimeoItemsVideoHLSPlayerFF={
                                                                vimeoItemsVideoHLSPlayerFF
                                                            }
                                                            videoUrl={carouselItem.url || ''}
                                                            heightOffset={0}
                                                            paused={index !== activeSlide - 1}
                                                            width={carouselItem.width}
                                                            height={carouselItem.height}
                                                        />
                                                    );
                                                }
                                            } else if (
                                                carouselItem.__type === 'viewInRoom' &&
                                                isPhoto(carouselItems[0])
                                            ) {
                                                child = (
                                                    <ViewInRoom
                                                        user={user}
                                                        item={item}
                                                        viewInRoomPhoto={carouselItem}
                                                        mainImagePath={{
                                                            imagePath: carouselItems[0].largePath,
                                                            masterOrZoomPath:
                                                                carouselItems[0].masterOrZoomPath,
                                                        }}
                                                    />
                                                );
                                            } else if (carouselItem.__type === 'requestMore') {
                                                child = (
                                                    <HeroCarouselRequestMore
                                                        user={user}
                                                        item={item}
                                                        currency={currency}
                                                        disableRequestMoreImagesModal={
                                                            disableModals
                                                        }
                                                    />
                                                );
                                            }

                                            return (
                                                <CarouselItemWrapper
                                                    key={index}
                                                    type={carouselItem.__type}
                                                    openZoomModal={openGalleryModalAtActiveSlide}
                                                    dataTn={`pdp-image-carousel-image-${index + 1}`}
                                                >
                                                    {child}
                                                </CarouselItemWrapper>
                                            );
                                        })}
                                    </ScrollContainer>
                                    <HeroCarouselArrow type="next" onArrowClick={onArrowClick} />
                                </div>
                            </InternalScrollCarouselProvider>
                        </div>

                        <PdpSharedMediaCountPill
                            photoCount={photoCount}
                            hasVideo={hasVideo}
                            onClick={disableModals ? undefined : openGalleryModalFromPill}
                            dataTn="pdp-image-carousel-media-pill"
                        />

                        {imageOverlay && (
                            <div
                                className={classnames(
                                    dibsCss.absolute,
                                    dibsCss.topXsmall,
                                    dibsCss.rightXsmall,
                                    dibsCss.zBump,
                                    dibsCss.flex,
                                    dibsCss.flexCol,
                                    dibsCss.gapXsmall
                                )}
                            >
                                {imageOverlay}
                            </div>
                        )}
                    </div>
                    {children && (
                        <div className={classnames(dibsCss.flex, dibsCss.justifyCenter)}>
                            {children}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

type Props = {
    item: PdpRespHeroCarousel_item$key;
    user: PdpRespHeroCarousel_user$key | null | undefined;
    viewer: PdpRespHeroCarousel_viewer$key;
    currency: string;
    disableModals?: boolean;
    afterDisplay: boolean;
    selectedSkuId?: string | null;
    imageOverlay?: ReactNode;
    enableSticky?: boolean;
    children?: ReactNode;
};

export const PdpRespHeroCarousel: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    user,
    currency,
    disableModals,
    afterDisplay,
    selectedSkuId,
    imageOverlay,
    enableSticky,
    children,
}) => {
    const item = useFragment(itemFragment, itemRef);

    const { videoVimeo, videoMux, hasPrivateListing, seller } = item;

    const sortedPhotos = (item.sortedPhotos || []).filter(filterNulls);
    const viewInRoomPhotos = (item.viewInRoomPhotos || []).filter(filterNulls);
    const mediaItems = useItemMediaItems({
        item,
        sortedPhotos,
        videoVimeo,
        muxVideo: videoMux,
        viewInRoomPhotos,
        selectedSkuId,
    });

    const showRequestMore = !hasPrivateListing && seller?.isAccessible;
    const carouselItems: CarouselItem[] = useMemo(() => {
        return showRequestMore ? [...mediaItems, { __type: 'requestMore' }] : mediaItems;
    }, [mediaItems, showRequestMore]);

    const mainPhoto = sortedPhotos?.[0];

    return (
        <>
            <div
                className={classnames(
                    styles.printHide,
                    styles.carouselStretch,
                    dibsCss.flex,
                    dibsCss.flex1,
                    dibsCss.flexCol,
                    dibsCss.wFull
                )}
            >
                <ScrollCarouselProvider
                    isInfinite
                    totalItems={carouselItems.length}
                    largestItemsToShow={THUMBS_VISIBLE}
                    stepSize={1}
                >
                    <PdpRespHeroCarouselInner
                        viewer={viewerRef}
                        item={itemRef}
                        user={user}
                        carouselItems={carouselItems}
                        currency={currency}
                        disableModals={disableModals}
                        afterDisplay={afterDisplay}
                        selectedSkuId={selectedSkuId}
                        imageOverlay={imageOverlay}
                        enableSticky={enableSticky}
                    >
                        {children}
                    </PdpRespHeroCarouselInner>
                </ScrollCarouselProvider>
            </div>

            {/* Print view */}
            {afterDisplay && mainPhoto?.alt && mainPhoto?.masterOrZoomPath && (
                <div className={styles.printShow}>
                    <img
                        src={mainPhoto.masterOrZoomPath}
                        alt={mainPhoto.alt}
                        className={classnames(
                            dibsCss.block,
                            dibsCss.wFull,
                            dibsCss.objectScaleDown,
                            styles.printImage
                        )}
                    />
                </div>
            )}
        </>
    );
};
